import "./App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Top8Provider } from "./context/Top8Context";
import ProtectedRoute from "./components/ProtectedRoute";
import InstallHint from "./components/InstallHint";
import GlobalSongControl from "./components/GlobalSongControl";
import { trackEvent } from "./lib/track";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Landing from "./pages/Landing";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Search from "./pages/Search";
import TitleDetail from "./pages/TitleDetail";
import Person from "./pages/Person";
import MyList from "./pages/MyList";
import Favorites from "./pages/Favorites";
import GenreBrowse from "./pages/GenreBrowse";
import FriendsFavorites from "./pages/FriendsFavorites";
import PodcastDetail from "./pages/PodcastDetail";
import CommunitySpace from "./pages/CommunitySpace";
import ThreadView from "./pages/ThreadView";
import Settings from "./pages/Settings";
import SubmitFilm from "./pages/SubmitFilm";
import Admin from "./pages/Admin";
import UserManagement from "./pages/UserManagement";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import ProfilePage from "./pages/ProfilePage";
import FriendsPage from "./pages/FriendsPage";
import FriendRequestsPage from "./pages/FriendRequestsPage";
import FAQ from "./pages/FAQ";
import ForCreators from "./pages/ForCreators";
import CanonicalTag from "./components/CanonicalTag";
import SpotlightIndex from "./pages/spotlight/SpotlightIndex";
import SpotlightDetail from "./pages/spotlight/SpotlightDetail";
import RegionPrompt from "./components/RegionPrompt";

const LAST_ROUTE_KEY = "fh_last_route";
const RESTORE_ROUTES = ["/title/", "/person/", "/my-list", "/search", "/genre/", "/friends-favorites", "/podcast/"];
// Compute the one-shot restore target ONCE at module load, before RouteTracker can overwrite it.
const _savedInitial = (() => { try { return localStorage.getItem(LAST_ROUTE_KEY); } catch { return null; } })();
let pendingRestore = (_savedInitial && _savedInitial !== "/" && RESTORE_ROUTES.some((r) => _savedInitial.startsWith(r))) ? _savedInitial : null;

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    if (location.pathname !== "/") pendingRestore = null; // restore only applies to a cold start on "/"
    const skip = ["/login", "/register", "/forgot-password", "/reset-password", "/onboarding"];
    if (!skip.some((s) => location.pathname.startsWith(s))) {
      localStorage.setItem(LAST_ROUTE_KEY, path);
    }
    trackEvent("page_view", { path: location.pathname });
  }, [location]);
  return null;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return null;
  if (user) return <Navigate to={user.onboarded ? "/" : "/onboarding"} replace />;
  return children;
}

function RootRoute() {
  const { user, ready } = useAuth();
  // Capture the restore target once for this mount (stable across StrictMode double-render).
  const [restoreTo] = useState(() => pendingRestore);
  useEffect(() => { pendingRestore = null; }, []);
  if (!ready || user === null) return null;
  if (!user) return <Landing />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (restoreTo) return <Navigate to={restoreTo} replace />;
  return <Home />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <Top8Provider>
          <BrowserRouter>
            <RouteTracker />
            <CanonicalTag />
            <GlobalSongControl />
            <RegionPrompt />
            <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
              <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
              <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>} />
              <Route path="/" element={<RootRoute />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/creators" element={<ForCreators />} />
              <Route path="/indie" element={<SpotlightIndex kind="indie" />} />
              <Route path="/indie/:slug" element={<SpotlightDetail kind="indie" />} />
              <Route path="/podcasts" element={<SpotlightIndex kind="podcasts" />} />
              <Route path="/podcasts/:slug" element={<SpotlightDetail kind="podcasts" />} />
              <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
              <Route path="/genre/:slug" element={<ProtectedRoute><GenreBrowse /></ProtectedRoute>} />
              <Route path="/podcast/:slug" element={<ProtectedRoute><PodcastDetail /></ProtectedRoute>} />
              <Route path="/friends-favorites" element={<ProtectedRoute><FriendsFavorites /></ProtectedRoute>} />
              <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
              <Route path="/title/:id" element={<ProtectedRoute><TitleDetail /></ProtectedRoute>} />
              <Route path="/community/film/:slug" element={<ProtectedRoute><CommunitySpace /></ProtectedRoute>} />
              <Route path="/community/film/:slug/thread/:tid" element={<ProtectedRoute><ThreadView /></ProtectedRoute>} />
              <Route path="/person/:id" element={<ProtectedRoute><Person /></ProtectedRoute>} />
              <Route path="/my-list" element={<ProtectedRoute><MyList /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
              <Route path="/friends/requests" element={<ProtectedRoute><FriendRequestsPage /></ProtectedRoute>} />
              <Route path="/profile/:username" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/submit" element={<SubmitFilm />} />
              <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <InstallHint />
          <Toaster position="bottom-right" toastOptions={{ style: { background: "var(--bg-surface)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }, descriptionClassName: "dial-toast-desc" }} />
          </Top8Provider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
